import React, { useState, useEffect, useRef, useCallback } from "react";
import ResponsiveHero from "../components/ResponsiveHero";
import MobileHero from "../components/MobileHero";
import Slider from "react-slick";
import { Helmet } from "react-helmet";
import ImageOptimizer from "../components/ImageOptimizer";

// Importar las imágenes originales como fallback
import Fondo1Original from "../images/Fondos/fondo1.png";
import Fondo2Original from "../images/Fondos/fondo2.png";
import Fondo3Original from "../images/Fondos/fondo3.png";

// URLs de las imágenes WebP optimizadas
const Fondo1WebP = "/images/fondo1.webp";
const Fondo2WebP = "/images/fondo2.webp";
const Fondo3WebP = "/images/fondo3.webp";

const HeroSection = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [, setActiveSlide] = useState(0);
  const [slidesReady, setSlidesReady] = useState(false);
  const [supportsWebP, setSupportsWebP] = useState(true);
  const sliderRef = useRef(null);
  
  // Detectar soporte de WebP
  useEffect(() => {
    const checkWebPSupport = async () => {
      try {
        const img = new Image();
        img.onload = () => setSupportsWebP(img.width > 0 && img.height > 0);
        img.onerror = () => setSupportsWebP(false);
        img.src = 'data:image/webp;base64,UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA==';
      } catch (err) {
        setSupportsWebP(false);
      }
    };
    
    checkWebPSupport();
  }, []);
  
  // Obtener URLs de imágenes según soporte del navegador
  const getImageSrc = useCallback((index) => {
    if (supportsWebP) {
      return [Fondo1WebP, Fondo2WebP, Fondo3WebP][index];
    } else {
      return [Fondo1Original, Fondo2Original, Fondo3Original][index];
    }
  }, [supportsWebP]);
  
  // Precargar la primera imagen para mejorar LCP (Largest Contentful Paint)
  useEffect(() => {
    const img = new Image();
    img.src = getImageSrc(0);
    img.onload = () => setSlidesReady(true);
    img.onerror = () => {
      // Si falla la carga de WebP, intenta con la original
      if (supportsWebP) {
        const fallbackImg = new Image();
        fallbackImg.src = Fondo1Original;
        fallbackImg.onload = () => setSlidesReady(true);
        fallbackImg.onerror = () => setSlidesReady(true); // Mostrar de todos modos
      } else {
        setSlidesReady(true);
      }
    };
    
    // Cargar las demás imágenes en segundo plano
    setTimeout(() => {
      [1, 2].forEach(index => {
        const img = new Image();
        img.src = getImageSrc(index);
      });
    }, 1000);
  }, [supportsWebP, getImageSrc]);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);

    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  const settings2 = {
    infinite: true,
    speed: 1000, // Aumentamos el tiempo de transición para que se vea más suave
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000, // Aumentamos el tiempo entre slides para reducir el procesamiento
    fade: true,
    arrows: false,
    beforeChange: (_, next) => setActiveSlide(next),
    lazyLoad: 'progressive',
  };

  // Array de imágenes de fondo
  const backgroundImages = [0, 1, 2];

  return (
    <>
      <Helmet>
        <link rel="preload" href={getImageSrc(0)} as="image" fetchpriority="high" />
        <style>{`
          .loading-spinner {
            border: 4px solid rgba(255, 255, 255, 0.3);
            border-radius: 50%;
            border-top: 4px solid #141836;
            width: 40px;
            height: 40px;
            animation: spin 1s linear infinite;
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          .fade-in {
            animation: fadeIn 0.5s ease-in forwards;
          }
          
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          .slick-slide, .slick-track {
            transform: translate3d(0, 0, 0);
            backface-visibility: hidden;
            perspective: 1000px;
            will-change: transform;
          }
        `}</style>
      </Helmet>
      
      <section
        id="home"
        className={`w-full relative mb-16 ${isMobile ? 'h-screen' : 'h-[70vh]'}`}
      >
        {!slidesReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#141836] z-10">
            <div className="loading-spinner"></div>
          </div>
        )}
        
        <div className={`w-full h-full ${slidesReady ? 'fade-in' : 'opacity-0'}`}>
          <Slider ref={sliderRef} {...settings2} className="w-full h-full">
            {backgroundImages.map((index) => (
              <div className="relative h-full" key={index}>
                <ImageOptimizer
                  src={getImageSrc(index)}
                  alt={`Fondo de Heroe ${index + 1}`}
                  objectFit={isMobile ? 'contain' : 'cover'}
                  priority={index === 0}
                />
                <div 
                  className="absolute inset-0"
                  style={{
                    background: isMobile ? 
                      `linear-gradient(180deg, rgba(20, 24, 54, 0.8) 0%, rgba(20, 24, 54, 0.6) 50%, rgba(20, 24, 54, 0.8) 100%)` :
                      `
                        linear-gradient(90deg, rgba(20, 24, 54, 0.95) 0%, rgba(20, 24, 54, 0.2) 20%, rgba(20, 24, 54, 0) 35%, rgba(20, 24, 54, 0) 65%, rgba(20, 24, 54, 0.2) 80%, rgba(20, 24, 54, 0.95) 100%),
                        linear-gradient(0deg, rgba(20, 24, 54, 0.95) 0%, rgba(20, 24, 54, 0.2) 20%, rgba(20, 24, 54, 0) 35%, rgba(20, 24, 54, 0) 65%, rgba(20, 24, 54, 0.2) 80%, rgba(20, 24, 54, 0.95) 100%)
                      `
                  }}
                />
              </div>
            ))}
          </Slider>
        </div>

        {isMobile ? <MobileHero /> : <ResponsiveHero />}
      </section>
    </>
  );
};

export default HeroSection;
